import { useEffect, useState, useCallback } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";

const AddTaskModalPopup = ({
  onSubmit,
  onEdit,
  editTask,
  mode,
  setSelectedTaskData,
  setMode,
}) => {
  const { userDetails } = useSelector((state) => state?.user);
  const { name } = userDetails?.data?.user || {};
  const userId = userDetails?.data?.user?._id;
  const userToken = useSelector((state) => state?.auth?.userToken);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState([]);
  const [assignedBy, setAssignedBy] = useState(null);
  const [status, setStatus] = useState(null);
  const [priority, setPriority] = useState(null);
  const [dueDate, setDueDate] = useState("");
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const statusOptions = [
    { value: "to-do", label: "To do" },
    { value: "in-progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
  ];

  const priorityOptions = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
  ];

  // Fetch departments and users in one function to avoid dependency issues
  const fetchDepartmentsAndUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      // Step 1: Fetch all departments
      const departmentsEndpoint = `${process.env.REACT_APP_BASE_URL}/api/departments`;
      
      const departmentsResponse = await axios.get(departmentsEndpoint, {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      });

      console.log("Departments response:", departmentsResponse.data);
      if (departmentsResponse.data?.status === "success") {
        const departmentsList = Array.isArray(departmentsResponse.data.data)
          ? departmentsResponse.data.data
          : departmentsResponse.data.data?.departments || [];
        
        // Find the development department
        const devDepartment = departmentsList.find(dept => dept.type === "development");
        
        if (devDepartment) {
          console.log("Found development department:", devDepartment._id);
          const departmentId = devDepartment._id;
          
          // Step 2: Fetch users from the development department
          try {
            const usersEndpoint = `${process.env.REACT_APP_BASE_URL}/api/users?fields=_id lastName firstName employeeId department -attendance -performanceReviews&department=${departmentId}`;
            
            const usersResponse = await axios.get(usersEndpoint, {
              headers: {
                Authorization: `Bearer ${userToken}`,
              },
            });

            console.log("Development users response:", usersResponse.data);
            if (usersResponse.data?.status === "success") {
              const usersList = Array.isArray(usersResponse.data.data)
                ? usersResponse.data.data
                : usersResponse.data.data?.users || [];
              
              console.log(`Found ${usersList.length} users in development department`);
              setUsers(usersList || []);
            } else {
              console.error("Failed to fetch development users:", usersResponse.data);
              toast.error("Failed to fetch users");
            }
          } catch (userError) {
            console.error("Error fetching development users:", userError);
            toast.error("Error fetching users");
            setIsError(true);
          }
        } else {
          console.error("Development department not found");
          toast.error("Could not find development department");
        }
      } else {
        console.error("Failed to fetch departments:", departmentsResponse.data);
        toast.error("Failed to fetch departments");
      }
    } catch (error) {
      console.error("Error in department/users workflow:", error);
      toast.error("Error loading data");
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  }, [userToken]);

  useEffect(() => {
    if (userToken) {
      fetchDepartmentsAndUsers();
    }
  }, [userToken, fetchDepartmentsAndUsers]);

  useEffect(() => {
    if (mode === "edit" && editTask) {
      setTitle(editTask.title);
      setDescription(editTask.description);
      // Handle multiple assignees
      setAssignedTo(
        editTask.assignedTo?.map((user) => ({
          label: `${user.firstName || ""} ${user.lastName || ""}`,
          value: user._id,
        })) || []
      );
      setAssignedBy({
        label: `${editTask.assignedBy?.firstName || ""} ${
          editTask.assignedBy?.lastName || ""
        }`,
        value: editTask.assignedBy?._id,
      });
      setStatus({
        value: editTask.status,
        label:
          editTask.status?.charAt(0)?.toUpperCase() +
          editTask.status?.slice(1)?.replace("_", " "),
      });
      setPriority({
        value: editTask.priority,
        label:
          editTask.priority?.charAt(0)?.toUpperCase() +
          editTask.priority?.slice(1),
      });
      setDueDate(editTask.dueDate?.split("T")[0] || "");
    } else {
      setTitle("");
      setDescription("");
      setAssignedTo([]);
      setAssignedBy({
        label: name,
        value: userId,
      });
      setStatus(null);
      setPriority(null);
      setDueDate("");
    }
    // console.log("assign to dropdown users are------>>>", assignedTo);
  }, [mode, editTask, name, userId]);

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (
      !title ||
      !description ||
      !assignedTo ||
      // !status ||
      !priority ||
      !dueDate
    ) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const taskData = {
      title: title,
      description: description,
      assignedTo: assignedTo.map((user) => user.value), // Extract array of user IDs
      assignedBy: assignedBy?.value,
      status: status?.value || "to-do",
      priority: priority?.value,
      dueDate,
    };

    if (mode === "edit") {
      onEdit(taskData);
    } else {
      onSubmit(taskData);
    }

    const modalId = "edit_task";
    const modalElement = document.getElementById(modalId);
    const closeButton = modalElement.querySelector(".btn-close");
    if (closeButton) closeButton.click();

    // Reset form
    setTitle("");
    setDescription("");
    setAssignedTo(null);
    setAssignedBy(null);
    setStatus(null);
    setPriority(null);
    setDueDate("");
  };

  const dropdownStyle = {
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "#cb6ce6"
        : state.isFocused
        ? "#f8defff5"
        : undefined,
      color: state.isSelected ? "#fff" : undefined,
      "&:active": {
        backgroundColor: "#f8defff5",
      },
    }),
  };

  const resetSelection = useCallback(() => {
    setSelectedTaskData(null);
    setMode(null);
  }, [setSelectedTaskData, setMode]);

  useEffect(() => {
    const modalElement = document.getElementById("edit_task");

    const handleHidden = () => {
      resetSelection();
    };

    if (modalElement) {
      modalElement.addEventListener("hidden.bs.modal", handleHidden);
    }

    return () => {
      if (modalElement) {
        modalElement.removeEventListener("hidden.bs.modal", handleHidden);
      }
    };
  }, [resetSelection]);

  return (
    <>
      <div id="edit_task" className="modal custom-modal fade" role="dialog">
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {mode === "edit" ? "Edit Task" : "Add Task"}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetSelection}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleFormSubmit}>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Title <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Description <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control py-2"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    required
                    rows="2.5"
                  />
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Assigned To <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Assignees"
                    value={assignedTo}
                    onChange={(options) => setAssignedTo(options)}
                    options={users.map((user) => ({
                      label: `${user.firstName} ${user.lastName}`,
                      value: user._id,
                    }))}
                    styles={dropdownStyle}
                    isLoading={isLoading}
                    noOptionsMessage={() =>
                      isError ? "Error loading users" : "No users found"
                    }
                    isMulti // Enable multiple selection
                    required
                  />
                </div>
                {mode === "edit" ? (
                  <div className="input-block mb-3">
                    <label className="col-form-label">
                      Status <span className="text-danger">*</span>
                    </label>
                    <Select
                      placeholder="Select Status"
                      value={status}
                      onChange={(option) => setStatus(option)}
                      options={statusOptions}
                      styles={dropdownStyle}
                    />
                  </div>
                ) : (
                  ""
                )}
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Priority <span className="text-danger">*</span>
                  </label>
                  <Select
                    placeholder="Select Priority"
                    value={priority}
                    onChange={(option) => setPriority(option)}
                    options={priorityOptions}
                    styles={dropdownStyle}
                    required
                  />
                </div>
                <div className="input-block mb-3">
                  <label className="col-form-label">
                    Due Date <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    type="date"
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                    required
                  />
                </div>
                <div className="submit-section">
                  <button className="btn btn-primary submit-btn" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddTaskModalPopup;